import React from "react";
import { motion } from "framer-motion";
import { ChevronDown, ChevronUp, Trash2, Search } from "lucide-react";
import favicon from "../assets/favicon_new.png";
import EditableDataDisplay from "./EditableDataDisplay";
import DocumentMetaLine from "./DocumentMetaLine";

// SearchInput component (extracted from Dashboard.js)
const SearchInput = React.memo(
  ({
    onSearchChange,
    onClearSearch,
    isDarkMode,
    searchResultCount,
    hasSearchQuery,
    isLoading,
    searchValue,
  }) => {
    const inputRef = React.useRef(null);
    const [inputValue, setInputValue] = React.useState(searchValue || "");
    const searchTimeoutRef = React.useRef(null);

    // Only update local state when searchValue changes from external source (not during typing)
    React.useEffect(() => {
      if (searchValue !== inputValue) {
        setInputValue(searchValue || "");
      }
    }, [searchValue]);

    React.useEffect(() => {
      const handleKeyDown = (e) => {
        if (e.key === "Escape") {
          onClearSearch();
        }
      };
      document.addEventListener("keydown", handleKeyDown);
      return () => document.removeEventListener("keydown", handleKeyDown);
    }, [onClearSearch]);

    const handleInputChange = (e) => {
      const value = e.target.value;
      setInputValue(value);
      
      // Clear any existing timeout
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      
      // Set a new timeout for search execution
      searchTimeoutRef.current = setTimeout(() => {
        onSearchChange(value);
      }, 750); // 500ms delay - adjust this value as needed
    };

    const handleClear = () => {
      // Clear any pending search timeout
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      
      setInputValue("");
      onClearSearch();
      if (inputRef.current) {
        inputRef.current.focus();
      }
    };

    // Cleanup timeout on unmount
    React.useEffect(() => {
      return () => {
        if (searchTimeoutRef.current) {
          clearTimeout(searchTimeoutRef.current);
        }
      };
    }, []);

    return (
      <div className="relative">
        <div className="relative">
          <Search
            className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 ${
              isDarkMode ? "text-gray-400" : "text-gray-500"
            }`}
          />
          <input
            ref={inputRef}
            type="text"
            placeholder="Search documents..."
            onChange={handleInputChange}
            value={inputValue}
            className={`w-full pl-10 pr-10 py-2 border rounded-lg text-sm transition-colors ${
              isDarkMode
                ? "bg-gray-700 border-gray-600 text-gray-200 placeholder-gray-400 focus:border-blue-500 focus:ring-blue-500"
                : "bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:border-blue-500 focus:ring-blue-500"
            }`}
          />
          {hasSearchQuery && (
            <button
              onClick={handleClear}
              className={`absolute right-3 top-1/2 transform -translate-y-1/2 p-1 rounded-full transition-colors ${
                isDarkMode
                  ? "text-gray-400 hover:text-gray-200 hover:bg-gray-600"
                  : "text-gray-500 hover:text-gray-700 hover:bg-gray-200"
              }`}
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          )}
        </div>
        {hasSearchQuery && (
          <div
            className={`text-xs mt-1 ${
              isDarkMode ? "text-gray-400" : "text-gray-500"
            }`}
          >
            {isLoading
              ? "Searching..."
              : `${searchResultCount} document${
                  searchResultCount === 1 ? "" : "s"
                } found`}
          </div>
        )}
      </div>
    );
  }
);

SearchInput.displayName = "SearchInput";

// MediaViewer component (extracted from Dashboard.js)
const MediaViewer = ({
  doc,
  isDarkMode,
  presignedUrls = {},
  loadingUrls = {},
  fetchPresignedUrls,
  documentViewModes = {},
}) => {
  React.useEffect(() => {
    // Fetch presigned URL if we don't have it yet and fetchPresignedUrls is available
    if (
      fetchPresignedUrls &&
      !presignedUrls[doc.id] &&
      !loadingUrls[doc.id]
    ) {
      fetchPresignedUrls([doc.id]);
    }
  }, [doc.id, presignedUrls, loadingUrls, fetchPresignedUrls]);

  // More flexible media check
  const hasMedia = doc.has_media || doc.media || doc.file_type;
  
  if (!hasMedia) {
    return null;
  }

  const presignedData = presignedUrls[doc.id];
  const isLoadingUrl = loadingUrls[doc.id];
  const viewMode = documentViewModes[doc.id] || "data";

  // Only show media in image view mode for this document
  if (viewMode !== "image") {
    return null;
  }

  return (
    <div className="mb-4">
      {isLoadingUrl ? (
        <div className="flex items-center justify-center py-8">
          <div className="flex items-center space-x-2">
            <div className="relative w-7 h-7 mb-1">
              <motion.div
                className="w-full h-full"
                animate={{ rotate: 360 }}
                transition={{
                  duration: 2,
                  ease: "linear",
                  repeat: Infinity,
                }}
              >
                <img
                  src={favicon}
                  alt="Loading"
                  className="w-full h-full object-contain"
                  onError={(e) => {
                    console.log("Image failed to load:", e.target.src);
                    e.target.style.display = "none";
                  }}
                />
              </motion.div>
            </div>
            <span
              className={`text-sm ${
                isDarkMode ? "text-gray-300" : "text-gray-600"
              }`}
            >
              Loading media...
            </span>
          </div>
        </div>
             ) : presignedData ? (
         <div className="mt-4">
           <div
             className={`relative rounded-lg overflow-hidden border ${
               isDarkMode ? "border-gray-600" : "border-gray-300"
             } shadow-lg`}
           >
             {doc.file_type === "pdf" ? (
               <div className="relative">
                 <iframe
                   src={`${presignedData.presigned_url}#toolbar=1&navpanes=1&scrollbar=1`}
                   className="w-full h-[70vh] min-h-[500px]"
                   title={`PDF: ${doc.name}`}
                   loading="lazy"
                   allow="fullscreen"
                 />
               </div>
             ) : (
               <img
                 src={presignedData.presigned_url}
                 alt={`Document: ${doc.name}`}
                 className="w-full h-auto object-contain"
                 onError={(e) => {
                   console.log("Image failed to load:", e.target.src);
                   e.target.style.display = "none";
                 }}
               />
             )}
           </div>
         </div>
      ) : (
        <div
          className={`text-center py-8 rounded-lg border ${
            isDarkMode
              ? "border-gray-600 bg-gray-800 text-gray-400"
              : "border-gray-300 bg-gray-50 text-gray-600"
          }`}
        >
          <p>No media available</p>
        </div>
      )}
    </div>
  );
};

const HistorySection = ({
  // Props for state and data
  searchQuery,
  totalDocuments,
  isLoadingHistory,
  documentHistory,
  expandedDocs,
  setExpandedDocs,
  reprocessingDocuments,
  presignedUrls,
  loadingUrls,
  paginationInfo,
  currentPage,
  resultViewMode,
  documentViewModes,
  editingDocuments,
  savingDocuments,
  filteredDocuments,
  isDarkMode,
  themeClasses,
  
  // Alternative prop names from parent (fallbacks)
  searchTerm,
  setSearchTerm,
  companies,
  selectedCompany,
  setSelectedCompany,

  // Props for handlers
  handleSearchChange,
  clearSearch,
  handleManualRefresh,
  toggleDocExpand,
  handleReprocessDocument,
  handleDeleteDocument,
  handleDocumentViewModeChange,
  copyToClipboard,
  handleEditDocument,
  downloadAsCSV,
  toggleResultViewMode,
  handleSaveDocument,
  handleCancelEdit,
  fetchDocumentHistory,
  historyStartDate,
  historyEndDate,
  historyStatusFilter,
  historyStatusCounts,
  onHistoryDateFilterChange,
  onHistoryStatusFilterChange,
  onExportHistoryToExcel,
  isExportingHistoryExcel,
  formatDateTime,
  hasFeature,
  fetchPresignedUrls,
}) => {
  // Safe defaults to prevent runtime errors when props are absent
  const effectiveIsLoading = Boolean(isLoadingHistory);
  const effectiveDocumentHistory = Array.isArray(documentHistory) ? documentHistory : [];
  const effectiveFilteredDocuments = Array.isArray(filteredDocuments) ? filteredDocuments : effectiveDocumentHistory;
  const effectiveExpandedDocs = expandedDocs || {};
  const effectiveReprocessing = reprocessingDocuments || {};
  const effectiveResultViewMode = resultViewMode || {};
  const effectiveDocumentViewModes = documentViewModes || {};
  const effectivePresignedUrls = presignedUrls || {};
  const effectiveLoadingUrls = loadingUrls || {};
  const effectivePagination = paginationInfo || null;
  const effectiveCurrentPage = currentPage || 1;
  const effectiveTotalDocuments = typeof totalDocuments === "number" ? totalDocuments : effectiveFilteredDocuments.length;
  const effectiveHistoryStartDate = historyStartDate || "";
  const effectiveHistoryEndDate = historyEndDate || "";
  const effectiveHistoryStatusFilter = historyStatusFilter || "all";
  const effectiveHistoryStatusCounts = historyStatusCounts || null;

  // Accept either searchQuery or searchTerm
  const effectiveSearchQuery = typeof searchQuery === "string" ? searchQuery : (typeof searchTerm === "string" ? searchTerm : "");

  const hasActiveHistoryFilters = Boolean(
    effectiveSearchQuery ||
      effectiveHistoryStartDate ||
      effectiveHistoryEndDate ||
      effectiveHistoryStatusFilter !== "all"
  );
  const hasHistoryDateFilterHandler = Boolean(onHistoryDateFilterChange);
  const hasHistoryStatusFilterHandler = Boolean(onHistoryStatusFilterChange);
  const hasHistoryExportHandler = Boolean(onExportHistoryToExcel);
  const isExportingExcel = Boolean(isExportingHistoryExcel);

  // Handlers with graceful fallbacks
  const onSearchChange = handleSearchChange || (setSearchTerm ? (v) => {
    setSearchTerm(v);
    // If we have fetchDocumentHistory available, trigger the search
    if (fetchDocumentHistory) {
      fetchDocumentHistory(1, v, true);
    }
  } : () => {});
  const onClearSearch = clearSearch || (setSearchTerm ? () => {
    setSearchTerm("");
    // If we have fetchDocumentHistory available, refresh without search
    if (fetchDocumentHistory) {
      fetchDocumentHistory(1, "", true);
    }
  } : () => {});
  // Determine availability of optional handlers
  const hasManualRefreshHandler = Boolean(handleManualRefresh || fetchDocumentHistory);
  const onManualRefresh = handleManualRefresh
    || (fetchDocumentHistory ? (() => fetchDocumentHistory(effectiveCurrentPage, effectiveSearchQuery)) : (() => {}));

  // Toggle expand: use provided toggle, or manage via setExpandedDocs if available
  const onToggleDocExpand = toggleDocExpand || (setExpandedDocs
    ? ((docId) => {
        setExpandedDocs((prev = {}) => ({ ...prev, [docId]: !prev[docId] }));
      })
    : (() => {}));

  const hasReprocessHandler = Boolean(handleReprocessDocument);
  const onReprocess = handleReprocessDocument || (() => {});

  const hasDeleteHandler = Boolean(handleDeleteDocument);
  const onDelete = handleDeleteDocument || (() => {});
  const onDocViewModeChange = handleDocumentViewModeChange || (() => {});
  const onCopy = copyToClipboard || (() => {});
  const onEdit = handleEditDocument || (() => {});
  const onDownloadCSV = downloadAsCSV || (() => {});
  const onToggleResultViewMode = toggleResultViewMode || (() => {});
  const onSave = handleSaveDocument || (() => {});
  const onCancel = handleCancelEdit || (() => {});
  const onFetchHistory = fetchDocumentHistory || (() => {});
  const onHasFeature = hasFeature || (() => false);
  const onFormatDateTime = formatDateTime || ((d, t) => `${d || ""} ${t || ""}`);
  const onDateFilterChange = onHistoryDateFilterChange || (() => {});
  const onStatusFilterChange = onHistoryStatusFilterChange || (() => {});
  const onExportExcel = onExportHistoryToExcel || (() => {});

  return (
    <div>
      {/* Header with Search and Refresh */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
        <div className="flex-1">
          <SearchInput
            key="document-search-input"
            onSearchChange={onSearchChange}
            onClearSearch={onClearSearch}
            isDarkMode={isDarkMode}
            searchResultCount={effectiveTotalDocuments}
            hasSearchQuery={effectiveSearchQuery !== ""}
            isLoading={effectiveIsLoading}
            searchValue={effectiveSearchQuery}
          />
        </div>
        <div className="flex items-center space ">
          <button
            onClick={onManualRefresh}
            disabled={effectiveIsLoading || !hasManualRefreshHandler}
            className={`p-2 rounded-lg transition-all duration-200 flex items-center justify-center ${
              effectiveIsLoading || !hasManualRefreshHandler
                ? "opacity-50 cursor-not-allowed"
                : isDarkMode
                ? "bg-gray-700 text-gray-200 hover:bg-gray-600 border border-gray-600"
                : "bg-gray-200 text-gray-700 hover:bg-gray-300 border border-gray-300"
            }`}
            title="Refresh"
          >
            {effectiveIsLoading ? (
              <div className="relative w-7 h-7">
                <motion.div
                  className="w-full h-full"
                  animate={{ rotate: 360 }}
                  transition={{
                    duration: 2,
                    ease: "linear",
                    repeat: Infinity,
                  }}
                >
                  <img
                    src={favicon}
                    alt="Loading"
                    className="w-full h-full object-contain"
                    onError={(e) => {
                      console.log(
                        "Image failed to load:",
                        e.target.src
                      );
                      e.target.style.display = "none";
                    }}
                  />
                </motion.div>
              </div>
            ) : (
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
            )}
          </button>
        </div>
      </div>

      {/* Date filters and export actions */}
      <div className="mb-6 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-3">
        <div className="flex flex-col sm:flex-row sm:items-end gap-3">
          <div className="flex flex-col">
            <label
              className={`text-xs mb-1 ${
                isDarkMode ? "text-gray-400" : "text-gray-600"
              }`}
            >
              From
            </label>
            <input
              type="date"
              value={effectiveHistoryStartDate}
              onChange={(e) =>
                onDateFilterChange(e.target.value, effectiveHistoryEndDate)
              }
              disabled={!hasHistoryDateFilterHandler}
              className={`px-3 py-2 rounded-lg border text-sm ${
                isDarkMode
                  ? "bg-gray-700 border-gray-600 text-gray-200"
                  : "bg-white border-gray-300 text-gray-800"
              } ${
                !hasHistoryDateFilterHandler ? "opacity-60 cursor-not-allowed" : ""
              }`}
            />
          </div>

          <div className="flex flex-col">
            <label
              className={`text-xs mb-1 ${
                isDarkMode ? "text-gray-400" : "text-gray-600"
              }`}
            >
              To
            </label>
            <input
              type="date"
              value={effectiveHistoryEndDate}
              onChange={(e) =>
                onDateFilterChange(effectiveHistoryStartDate, e.target.value)
              }
              disabled={!hasHistoryDateFilterHandler}
              className={`px-3 py-2 rounded-lg border text-sm ${
                isDarkMode
                  ? "bg-gray-700 border-gray-600 text-gray-200"
                  : "bg-white border-gray-300 text-gray-800"
              } ${
                !hasHistoryDateFilterHandler ? "opacity-60 cursor-not-allowed" : ""
              }`}
            />
          </div>

          <button
            onClick={() => onDateFilterChange("", "")}
            disabled={
              !hasHistoryDateFilterHandler ||
              (!effectiveHistoryStartDate && !effectiveHistoryEndDate)
            }
            className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
              !hasHistoryDateFilterHandler ||
              (!effectiveHistoryStartDate && !effectiveHistoryEndDate)
                ? "opacity-50 cursor-not-allowed"
                : isDarkMode
                ? "bg-gray-700 text-gray-200 hover:bg-gray-600 border border-gray-600"
                : "bg-gray-200 text-gray-700 hover:bg-gray-300 border border-gray-300"
            }`}
          >
            Clear dates
          </button>

          <div className="flex flex-col">
            <label
              className={`text-xs mb-1 ${
                isDarkMode ? "text-gray-400" : "text-gray-600"
              }`}
            >
              Status
            </label>
            <select
              value={effectiveHistoryStatusFilter}
              onChange={(e) => onStatusFilterChange(e.target.value)}
              disabled={!hasHistoryStatusFilterHandler}
              className={`px-3 py-2 rounded-lg border text-sm ${
                isDarkMode
                  ? "bg-gray-700 border-gray-600 text-gray-200"
                  : "bg-white border-gray-300 text-gray-800"
              } ${
                !hasHistoryStatusFilterHandler
                  ? "opacity-60 cursor-not-allowed"
                  : ""
              }`}
            >
              <option value="all">All</option>
              <option value="successful">Successful</option>
              <option value="failed">Failed</option>
            </select>
          </div>
        </div>

        <button
          onClick={onExportExcel}
          disabled={isExportingExcel || effectiveIsLoading || !hasHistoryExportHandler}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            isExportingExcel || effectiveIsLoading || !hasHistoryExportHandler
              ? "opacity-50 cursor-not-allowed"
              : isDarkMode
              ? "bg-green-600/30 text-green-300 hover:bg-green-500/40 border border-green-500/30"
              : "bg-green-100 text-green-700 hover:bg-green-200 border border-green-300"
          }`}
        >
          {isExportingExcel ? "Exporting..." : "Export Excel"}
        </button>
      </div>

      {hasActiveHistoryFilters && effectiveHistoryStatusCounts && (
        <div className="mb-6 flex flex-wrap items-center gap-2">
          <span
            className={`text-xs font-medium ${
              isDarkMode ? "text-gray-400" : "text-gray-600"
            }`}
          >
            Filtered summary:
          </span>
          {[
            {
              key: "total",
              label: "Total",
              value: effectiveHistoryStatusCounts.total,
              className: isDarkMode
                ? "bg-gray-700 text-gray-200 border-gray-600"
                : "bg-gray-100 text-gray-800 border-gray-300",
            },
            {
              key: "successful",
              label: "Successful",
              value: effectiveHistoryStatusCounts.successful,
              className: isDarkMode
                ? "bg-green-900/40 text-green-300 border-green-700"
                : "bg-green-100 text-green-800 border-green-300",
            },
            {
              key: "failed",
              label: "Failed",
              value: effectiveHistoryStatusCounts.failed,
              className: isDarkMode
                ? "bg-red-900/40 text-red-300 border-red-700"
                : "bg-red-100 text-red-800 border-red-300",
            },
            {
              key: "invalid",
              label: "Invalid",
              value: effectiveHistoryStatusCounts.invalid,
              className: isDarkMode
                ? "bg-orange-900/40 text-orange-300 border-orange-700"
                : "bg-orange-100 text-orange-800 border-orange-300",
            },
            {
              key: "pending",
              label: "Pending",
              value: effectiveHistoryStatusCounts.pending,
              className: isDarkMode
                ? "bg-yellow-900/40 text-yellow-300 border-yellow-700"
                : "bg-yellow-100 text-yellow-800 border-yellow-300",
            },
            {
              key: "push_failed",
              label: "Couldn't Push",
              value: effectiveHistoryStatusCounts.push_failed,
              className: isDarkMode
                ? "bg-amber-900/40 text-amber-300 border-amber-700"
                : "bg-amber-100 text-amber-800 border-amber-300",
            },
            {
              key: "other",
              label: "Other",
              value: effectiveHistoryStatusCounts.other,
              className: isDarkMode
                ? "bg-slate-800 text-slate-300 border-slate-600"
                : "bg-slate-100 text-slate-700 border-slate-300",
            },
          ]
            .filter((item) => item.key === "total" || item.value > 0)
            .map((item) => (
              <span
                key={item.key}
                className={`px-2.5 py-1 rounded-full text-xs font-medium border ${item.className}`}
              >
                {item.label}: {item.value}
              </span>
            ))}
        </div>
      )}

      {effectiveIsLoading ? (
        <div className="flex items-center justify-center py-8">
          <div className="flex items-center space-x-2">
            <div className="relative w-7 h-7 mb-1">
              <motion.div
                className="w-full h-full"
                animate={{ rotate: 360 }}
                transition={{
                  duration: 2,
                  ease: "linear",
                  repeat: Infinity,
                }}
              >
                <img
                  src={favicon}
                  alt="Loading"
                  className="w-full h-full object-contain"
                  onError={(e) => {
                    console.log("Image failed to load:", e.target.src);
                    e.target.style.display = "none";
                  }}
                />
              </motion.div>
            </div>
            <span
              className={`text-sm ${
                isDarkMode ? "text-gray-300" : "text-gray-600"
              }`}
            >
              Loading documents...
            </span>
          </div>
        </div>
      ) : effectiveFilteredDocuments.length === 0 ? (
        <div className="text-center py-8">
          {effectiveSearchQuery ? (
            <>
              <Search
                className={`mx-auto h-12 w-12 ${
                  isDarkMode ? "text-gray-600" : "text-gray-400"
                } mb-4`}
              />
              <p
                className={`text-lg font-medium ${
                  isDarkMode ? "text-gray-300" : "text-gray-700"
                }`}
              >
                No documents found
              </p>
              <p
                className={`text-sm ${
                  isDarkMode ? "text-gray-400" : "text-gray-600"
                } mt-1`}
              >
                Try adjusting your search terms
              </p>
            </>
          ) : (
            <p
              className={`text-gray-500 ${
                isDarkMode ? "text-gray-400" : "text-gray-600"
              } font-body`}
            >
              No document history available.
            </p>
          )}
        </div>
      ) : (
        <>
          <ul className="space-y-4 rounded-lg">
            {effectiveFilteredDocuments.map((doc) => (
              <li
                key={doc.id}
                className={` overflow-hidden rounded-xl shadow-sm transition-all duration-300 hover:shadow-md ${
                  isDarkMode ? "bg-gray-700" : "bg-gray-100"
                }`}
              >
                <div
                  className={`flex flex-col rounded sm:flex-row border ${
                    isDarkMode ? "border-slate-950" : "border-slate-280"
                  } sm:items-center sm:justify-between p-4`}
                >
                  <div
                    className="flex items-center space-x-4 cursor-pointer flex-1"
                    onClick={() => onToggleDocExpand(doc.id)}
                  >
                    <div
                      className={`w-3 h-3 rounded-full ${
                        doc.status === "Completed"
                          ? "bg-green-500"
                          : doc.status.toLowerCase() === "pending"
                          ? "bg-yellow-500"
                          : doc.status.toLowerCase() === "couldn't push"
                          ? "bg-orange-500"
                          : "bg-red-500"
                      }`}
                    ></div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2">
                        <span
                          className={`font-medium truncate ${
                            isDarkMode
                              ? "text-gray-200"
                              : "text-gray-800"
                          }`}
                        >
                          {doc.name}
                        </span>
                      </div>
                      <DocumentMetaLine
                        doc={doc}
                        isDarkMode={isDarkMode}
                        dateTime={onFormatDateTime(doc.date, doc.time)}
                      />
                    </div>
                  </div>
                  <div className="flex items-center mt-2 sm:mt-0 space-x-2">
                    <span
                      className={`px-3 py-1 rounded-full text-sm font-medium ${
                        doc.status === "Completed"
                          ? "bg-green-100 text-green-800"
                          : doc.status.toLowerCase() === "pending"
                          ? "bg-yellow-100 text-yellow-800"
                          : doc.status.toLowerCase() === "couldn't push"
                          ? "bg-orange-100 text-orange-800"
                          : "bg-red-100 text-red-800"
                      }`}
                    >
                      {doc.status}
                    </span>

                    {/* Reprocess Button - only show for completed documents and tally users */}
                    {doc.status === "Completed" &&
                      onHasFeature("tally_integration") &&
                      hasReprocessHandler && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onReprocess(doc);
                          }}
                          disabled={effectiveReprocessing[doc.id]}
                          className={`p-2 rounded-lg border border-slate-200 transition-all duration-200 ${
                            effectiveReprocessing[doc.id]
                              ? "opacity-50 cursor-not-allowed"
                              : isDarkMode
                              ? "text-blue-400 hover:bg-blue-500/20 hover:text-blue-300"
                              : "text-blue-600 hover:bg-blue-100 hover:text-blue-700"
                          }`}
                          title={
                            effectiveReprocessing[doc.id]
                              ? "Reprocessing..."
                              : "Reprocess document"
                          }
                        >
                          {effectiveReprocessing[doc.id] ? (
                            <div className="relative w-7 h-7">
                              <motion.div
                                className="w-full h-full"
                                animate={{ rotate: 360 }}
                                transition={{
                                  duration: 2,
                                  ease: "linear",
                                  repeat: Infinity,
                                }}
                              >
                                <img
                                  src={favicon}
                                  alt="Loading"
                                  className="w-full h-full object-contain"
                                  onError={(e) => {
                                    console.log(
                                      "Image failed to load:",
                                      e.target.src
                                    );
                                    e.target.style.display = "none";
                                  }}
                                />
                              </motion.div>
                            </div>
                          ) : (
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                              />
                            </svg>
                          )}
                        </button>
                      )}

                    {/* Delete Button */}
                    {hasDeleteHandler && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onDelete(doc.id);
                        }}
                        className={`p-2 rounded-lg transition-all duration-200 ${
                          isDarkMode
                            ? "text-red-400 hover:bg-red-500/20 hover:text-red-300"
                            : "text-red-600 hover:bg-red-100 hover:text-red-700"
                        }`}
                        title="Delete document"
                      >
                        <Trash2 size={16} />
                      </button>
                    )}

                    {(doc.status === "Completed" ||
                      doc.status.toLowerCase() === "pending" ||
                      doc.status.toLowerCase() === "couldn't push" ||
                      doc.status.toLowerCase() === "invalid document") &&
                      doc.gpt_result && (
                        <div
                          className="cursor-pointer"
                          onClick={() => onToggleDocExpand(doc.id)}
                        >
                          {effectiveExpandedDocs[doc.id] ? (
                            <ChevronUp
                              size={20}
                              className={`${
                                isDarkMode
                                  ? "text-gray-400"
                                  : "text-gray-500"
                              }`}
                            />
                          ) : (
                            <ChevronDown
                              size={20}
                              className={`${
                                isDarkMode
                                  ? "text-gray-400"
                                  : "text-gray-500"
                              }`}
                            />
                          )}
                        </div>
                      )}
                  </div>
                </div>
                {effectiveExpandedDocs[doc.id] &&
                  (doc.status === "Completed" ||
                    doc.status.toLowerCase() === "pending" ||
                    doc.status.toLowerCase() === "couldn't push" ||
                    doc.status.toLowerCase() === "invalid document") &&
                  doc.gpt_result && (
                    <div
                      className={`p-4 border rounded-b-lg border-t-0 ${
                        isDarkMode
                          ? "border-slate-950 bg-gray-800"
                          : "border-slate-280 bg-gray-50"
                      }`}
                    >
                      {/* View Mode Toggle for this document */}
                      {(doc.has_media || doc.media) && (
                        <div className="flex items-center justify-between mb-4">
                          <span
                            className={`text-sm font-medium ${
                              isDarkMode
                                ? "text-gray-300"
                                : "text-gray-700"
                            }`}
                          >
                            Content View:
                          </span>
                          <div
                            className={`flex items-center rounded-lg p-1 ${
                              isDarkMode ? "bg-gray-700" : "bg-gray-100"
                            }`}
                          >
                            <button
                              onClick={() =>
                                onDocViewModeChange(
                                  doc.id,
                                  "data"
                                )
                              }
                              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all duration-200 ${
                                (effectiveDocumentViewModes[doc.id] ||
                                  "data") === "data"
                                  ? isDarkMode
                                    ? "bg-blue-600 text-white shadow-sm"
                                    : "bg-blue-500 text-white shadow-sm"
                                  : isDarkMode
                                  ? "text-gray-300 hover:text-white hover:bg-gray-600"
                                  : "text-gray-600 hover:text-gray-900 hover:bg-gray-200"
                              }`}
                            >
                              <svg
                                className="w-3 h-3 inline mr-1"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                />
                              </svg>
                              Data
                            </button>
                            <button
                              onClick={() =>
                                onDocViewModeChange(
                                  doc.id,
                                  "image"
                                )
                              }
                              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all duration-200 ${
                                (effectiveDocumentViewModes[doc.id] ||
                                  "data") === "image"
                                  ? isDarkMode
                                    ? "bg-blue-600 text-white shadow-sm"
                                    : "bg-blue-500 text-white shadow-sm"
                                  : isDarkMode
                                  ? "text-gray-300 hover:text-white hover:bg-gray-600"
                                  : "text-gray-600 hover:text-gray-900 hover:bg-gray-200"
                              }`}
                            >
                              <svg
                                className="w-3 h-3 inline mr-1"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                                />
                              </svg>
                              Media
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Media Preview */}
                      <MediaViewer
                        doc={doc}
                        isDarkMode={isDarkMode}
                        presignedUrls={effectivePresignedUrls}
                        loadingUrls={effectiveLoadingUrls}
                        fetchPresignedUrls={fetchPresignedUrls}
                        documentViewModes={effectiveDocumentViewModes}
                      />

                      {/* GPT Results Section - only show in data view */}
                      {(effectiveDocumentViewModes[doc.id] || "data") ===
                        "data" && (
                        <>
                          <div className="flex justify-end mb-2 space-x-2 mt-4">
                            <button
                              onClick={() => onCopy(doc.gpt_result)}
                              className={`px-2 py-1 text-xs rounded flex items-center ${
                                isDarkMode
                                  ? "bg-gray-600 hover:bg-gray-500 text-gray-200"
                                  : "bg-gray-200 hover:bg-gray-300 text-gray-700"
                              }`}
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="w-3.5 h-3.5 mr-1"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <rect
                                  x="9"
                                  y="9"
                                  width="13"
                                  height="13"
                                  rx="2"
                                  ry="2"
                                ></rect>
                                <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"></path>
                              </svg>
                              Copy
                            </button>

                            {doc.status.toLowerCase() !== "invalid document" && (
                            <button
                              onClick={() => onEdit(doc.id)}
                              className={`px-2 py-1 text-xs rounded flex items-center ${
                                isDarkMode
                                  ? "bg-blue-600 hover:bg-blue-500 text-blue-200"
                                  : "bg-blue-200 hover:bg-blue-300 text-blue-700"
                              }`}
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="w-3.5 h-3.5 mr-1"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"></path>
                                <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                              </svg>
                              Edit
                            </button>
                            )}

                            {doc.status.toLowerCase() !== "invalid document" && (
                            <button
                              onClick={() => onDownloadCSV(doc.gpt_result)}
                              className={`px-2 py-1 text-xs rounded flex items-center ${
                                isDarkMode
                                  ? "bg-green-600/30 hover:bg-green-500/40 text-green-300"
                                  : "bg-green-100 hover:bg-green-200 text-green-700"
                              }`}
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="w-3.5 h-3.5 mr-1"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"></path>
                                <polyline points="7 10 12 15 17 10"></polyline>
                                <line x1="12" y1="15" x2="12" y2="3"></line>
                              </svg>
                              CSV
                            </button>
                            )}

                            <button
                              onClick={() => onToggleResultViewMode(doc.id)}
                              className={`px-2 py-1 text-xs rounded ${
                                isDarkMode
                                  ? "bg-gray-600 hover:bg-gray-500 text-gray-200"
                                  : "bg-gray-200 hover:bg-gray-300 text-gray-700"
                              }`}
                            >
                              Switch to {effectiveResultViewMode[doc.id] === "json" ? "Formatted" : "JSON"} View
                            </button>
                          </div>

                          {/* GPT Results Section */}
                          <div
                            className={`rounded-lg border border-slate-200 ${
                              isDarkMode
                                ? "bg-gray-900/50 border-slate-900"
                                : "bg-white border-slate-280"
                            } p-4`}
                          >
                            <div className="flex items-center mb-3">
                              <svg
                                className={`w-5 h-5 mr-2 ${
                                  isDarkMode ? "text-blue-400" : "text-blue-600"
                                }`}
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                />
                              </svg>
                              <span
                                className={`text-sm font-medium ${
                                  isDarkMode ? "text-gray-300" : "text-gray-700"
                                }`}
                              >
                                Extracted Data
                              </span>
                            </div>

                            {/* Conditional Rendering */}
                            {effectiveResultViewMode[doc.id] === "json" ? (
                              <pre
                                className={`whitespace-pre-wrap text-xs p-3 rounded ${
                                  isDarkMode
                                    ? "bg-black/30 text-gray-300"
                                    : "bg-gray-100 text-gray-800"
                                } overflow-x-auto`}
                              >
                                {(() => {
                                  try {
                                    let dataToDisplay = doc.gpt_result;
                                    if (typeof dataToDisplay === "string") {
                                      let cleanedData = dataToDisplay.trim();
                                      if (cleanedData.startsWith("```") ) {
                                        const match = cleanedData.match(/^```(?:json)?\s*([\s\S]*?)\s*```$/);
                                        if (match) cleanedData = match[1];
                                      }
                                      dataToDisplay = JSON.parse(cleanedData);
                                    }
                                    return JSON.stringify(dataToDisplay, null, 2);
                                  } catch (e) {
                                    return doc.gpt_result;
                                  }
                                })()}
                              </pre>
                            ) : (
                              <EditableDataDisplay
                                data={doc.gpt_result}
                                themeClasses={themeClasses}
                                isDarkMode={isDarkMode}
                                isEditing={effectiveExpandedDocs && (editingDocuments?.[doc.id] || false)}
                                isSaving={savingDocuments?.[doc.id] || false}
                                onEdit={() => onEdit(doc.id)}
                                onSave={(updatedData) => onSave(doc.id, updatedData)}
                                onCancel={() => onCancel(doc.id)}
                              />
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  )}
              </li>
            ))}
          </ul>

          {/* Pagination Controls */}
{effectivePagination && effectivePagination.total_pages > 1 && onFetchHistory !== (() => {}) && (
  <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
    {/* Mobile Layout */}
    <div className="flex flex-col space-y-4 sm:hidden">
      {/* Page Info */}
      <div
        className={`text-xs text-center ${
          isDarkMode ? "text-gray-400" : "text-gray-600"
        }`}
      >
        Page {effectiveCurrentPage} of {effectivePagination.total_pages}
      </div>
      
      {/* Navigation Buttons */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => {
            if (effectivePagination.has_prev) {
              onFetchHistory(effectiveCurrentPage - 1, effectiveSearchQuery);
            }
          }}
          disabled={!effectivePagination.has_prev}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            !effectivePagination.has_prev
              ? "opacity-50 cursor-not-allowed"
              : isDarkMode
              ? "bg-gray-700 text-gray-200 hover:bg-gray-600"
              : "bg-gray-200 text-gray-700 hover:bg-gray-300"
          }`}
        >
          Previous
        </button>

        {/* Current page indicator */}
        <div className="flex items-center space-x-1">
          {effectiveCurrentPage > 2 && (
            <>
              <button
                onClick={() => onFetchHistory(1, effectiveSearchQuery)}
                className={`w-8 h-8 rounded-md text-xs font-medium transition-colors ${
                  isDarkMode
                    ? "bg-gray-700 text-gray-200 hover:bg-gray-600"
                    : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                }`}
              >
                1
              </button>
              {effectiveCurrentPage > 3 && (
                <span className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}>
                  ...
                </span>
              )}
            </>
          )}
          
          {effectiveCurrentPage > 1 && (
            <button
              onClick={() => onFetchHistory(effectiveCurrentPage - 1, effectiveSearchQuery)}
              className={`w-8 h-8 rounded-md text-xs font-medium transition-colors ${
                isDarkMode
                  ? "bg-gray-700 text-gray-200 hover:bg-gray-600"
                  : "bg-gray-200 text-gray-700 hover:bg-gray-300"
              }`}
            >
              {effectiveCurrentPage - 1}
            </button>
          )}
          
          <button
            className={`w-8 h-8 rounded-md text-xs font-medium ${
              isDarkMode
                ? "bg-blue-600 text-white"
                : "bg-blue-500 text-white"
            }`}
          >
            {effectiveCurrentPage}
          </button>
          
          {effectiveCurrentPage < effectivePagination.total_pages && (
            <button
              onClick={() => onFetchHistory(effectiveCurrentPage + 1, effectiveSearchQuery)}
              className={`w-8 h-8 rounded-md text-xs font-medium transition-colors ${
                isDarkMode
                  ? "bg-gray-700 text-gray-200 hover:bg-gray-600"
                  : "bg-gray-200 text-gray-700 hover:bg-gray-300"
              }`}
            >
              {effectiveCurrentPage + 1}
            </button>
          )}
          
          {effectiveCurrentPage < effectivePagination.total_pages - 1 && (
            <>
              {effectiveCurrentPage < effectivePagination.total_pages - 2 && (
                <span className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}>
                  ...
                </span>
              )}
              <button
                onClick={() => onFetchHistory(effectivePagination.total_pages, effectiveSearchQuery)}
                className={`w-8 h-8 rounded-md text-xs font-medium transition-colors ${
                  isDarkMode
                    ? "bg-gray-700 text-gray-200 hover:bg-gray-600"
                    : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                }`}
              >
                {effectivePagination.total_pages}
              </button>
            </>
          )}
        </div>

        <button
          onClick={() => {
            if (effectivePagination.has_next) {
              onFetchHistory(effectiveCurrentPage + 1, effectiveSearchQuery);
            }
          }}
          disabled={!effectivePagination.has_next}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            !effectivePagination.has_next
              ? "opacity-50 cursor-not-allowed"
              : isDarkMode
              ? "bg-gray-700 text-gray-200 hover:bg-gray-600"
              : "bg-gray-200 text-gray-700 hover:bg-gray-300"
          }`}
        >
          Next
        </button>
      </div>
      
      {/* Document count for mobile */}
      <div
        className={`text-xs text-center ${
          isDarkMode ? "text-gray-400" : "text-gray-600"
        }`}
      >
        {(effectiveCurrentPage - 1) * 15 + 1} - {Math.min(effectiveCurrentPage * 15, effectiveTotalDocuments)} of {effectiveTotalDocuments}
      </div>
    </div>

    {/* Desktop Layout */}
    <div className="hidden sm:flex items-center justify-between">
      <div
        className={`text-sm ${
          isDarkMode ? "text-gray-400" : "text-gray-600"
        }`}
      >
        Showing {(effectiveCurrentPage - 1) * 15 + 1} to {Math.min(effectiveCurrentPage * 15, effectiveTotalDocuments)} of {effectiveTotalDocuments} documents
      </div>
      
      <div className="flex items-center space-x-2">
        <button
          onClick={() => {
            if (effectivePagination.has_prev) {
              onFetchHistory(effectiveCurrentPage - 1, effectiveSearchQuery);
            }
          }}
          disabled={!effectivePagination.has_prev}
          className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
            !effectivePagination.has_prev
              ? "opacity-50 cursor-not-allowed"
              : isDarkMode
              ? "bg-gray-700 text-gray-200 hover:bg-gray-600"
              : "bg-gray-200 text-gray-700 hover:bg-gray-300"
          }`}
        >
          Previous
        </button>

        {/* Page numbers for desktop */}
        {[...Array(Math.min(5, effectivePagination.total_pages))].map((_, index) => {
          const pageNum = Math.max(1, Math.min(effectivePagination.total_pages - 4, Math.max(1, effectiveCurrentPage - 2))) + index;
          if (pageNum > effectivePagination.total_pages) return null;
          return (
            <button
              key={pageNum}
              onClick={() => {
                if (pageNum !== effectiveCurrentPage) {
                  onFetchHistory(pageNum, effectiveSearchQuery);
                }
              }}
              className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                pageNum === effectiveCurrentPage
                  ? isDarkMode
                    ? "bg-blue-600 text-white"
                    : "bg-blue-500 text-white"
                  : isDarkMode
                  ? "bg-gray-700 text-gray-200 hover:bg-gray-600"
                  : "bg-gray-200 text-gray-700 hover:bg-gray-300"
              }`}
            >
              {pageNum}
            </button>
          );
        })}

        <button
          onClick={() => {
            if (effectivePagination.has_next) {
              onFetchHistory(effectiveCurrentPage + 1, effectiveSearchQuery);
            }
          }}
          disabled={!effectivePagination.has_next}
          className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
            !effectivePagination.has_next
              ? "opacity-50 cursor-not-allowed"
              : isDarkMode
              ? "bg-gray-700 text-gray-200 hover:bg-gray-600"
              : "bg-gray-200 text-gray-700 hover:bg-gray-300"
          }`}
        >
          Next
        </button>
      </div>
    </div>
  </div>
)}
        </>
      )}
    </div>
  );
};

export default HistorySection;
